import DateFnsUtils from '@date-io/date-fns';
import React, { useContext, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import ArrowLeft from '@material-ui/icons/ArrowBack';
import ArrowRight from '@material-ui/icons/ArrowForward';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { DatePicker } from '@material-ui/pickers/DatePicker';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import {
  ApiFilterOperation,
  NavigationContext,
  UserContext,
  UserSettingsContext,
} from '@eas/common-web';
import { useColumns as useCourtFilesAllColumns } from '@modules/es-file-court/all/court-file-all-columns';
import {
  COURT_FILES_ALL_IDENTIFIER,
  COURT_FILES_ALL_VERSION,
} from '@modules/es-file-court/all/court-files-all';
import { Me } from '@models';
import { EvidenceBrowserUrl } from '@enums';
import { DashboardBarChart, DashboardBarChartItem } from '../bar-chart';
import { DashboardButton } from '../button';
import { DashboardCard } from '../card';
import {
  FormattedAssistantBar,
  useAssistantEsFilesStatistics,
} from '../dashboard-hook';
import { DashboardDivider } from '../divider';
import {
  createInitialFilterState,
  fetchAssistantAutocomplete,
  fetchJudgeAutocomplete,
  getCurrentUserJudge,
  getFilterStateIndexFromColumnsByKey,
  resolvedEssRecordStates,
  unresolvedEssRecordStates,
} from './utils';

type AssistantStat = ReturnType<
  typeof useAssistantEsFilesStatistics
>['formattedData']['data'][number];

const MAX_YEAR = new Date().getFullYear();
const MIN_YEAR = 1990;

export function JudgeMyTeamEsFilesStatistics() {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const [year, setYear] = useState(MAX_YEAR);
  const [allYears, setAllYears] = useState(true);
  const [assistantData, setAssistantData] = useState<AssistantStat>();
  const [open, setOpen] = useState(false);

  const { formattedData } = useAssistantEsFilesStatistics(
    allYears ? undefined : year
  );

  const { navigate } = useContext(NavigationContext);
  const { getTableSettings, setTableSettings } =
    useContext(UserSettingsContext);

  const courtFilesAllColumns = useCourtFilesAllColumns();

  const config = {
    identifier: COURT_FILES_ALL_IDENTIFIER,
    version: COURT_FILES_ALL_VERSION,
    columns: courtFilesAllColumns,
    url: EvidenceBrowserUrl.COURT_FILES_ALL,
  };

  const handleBarClick = async (
    _barData: DashboardBarChartItem,
    index?: number
  ) => {
    const barData = _barData as FormattedAssistantBar;

    if (index === undefined || index < 0 || !barData) {
      return;
    }

    const essStates =
      barData.state === 'RESOLVED'
        ? resolvedEssRecordStates
        : unresolvedEssRecordStates;

    const essRecordStateFilter = {
      enabled: true,
      value: null,
      operation: ApiFilterOperation.OR,
      filters: essStates.map((value) => ({
        field: 'essRecord.state.id',
        operation: ApiFilterOperation.EQ,
        value,
      })),
      selectFilter: essStates,
      object: null,
      numberFilter: null,
    };

    const tableSettingsId = `${config.identifier}_TABLE`;
    const settings = getTableSettings(tableSettingsId, config.version);
    const filtersState = createInitialFilterState(config.columns);
    const judge = getCurrentUserJudge(user);

    const setFilterIfExists = (
      datakey: string,
      filterState: typeof filtersState[number]
    ) => {
      const filterIndex = getFilterStateIndexFromColumnsByKey(
        config.columns,
        datakey
      );

      if (filterIndex !== -1) {
        filtersState[filterIndex] = filterState;
      }
    };

    if (!allYears) {
      setFilterIfExists('number.year', {
        enabled: true,
        value: null,
        operation: ApiFilterOperation.AND,
        object: null,
        filters: [
          {
            field: 'number.year',
            operation: ApiFilterOperation.GTE,
            value: year,
          },
          {
            field: 'number.year',
            operation: ApiFilterOperation.LTE,
            value: year,
          },
        ],
        numberFilter: {
          from: year,
          to: year,
        },
      });
    }

    if (judge.id) {
      setFilterIfExists('judge.id', {
        enabled: true,
        value: judge.id,
        operation: ApiFilterOperation.EQ,
        object: await fetchJudgeAutocomplete(judge),
      });
    }

    setFilterIfExists('essRecord.state.id', essRecordStateFilter);

    setFilterIfExists('type.id', {
      enabled: true,
      value: barData.type,
      operation: ApiFilterOperation.EQ,
    });

    if (barData.assistantId) {
      setFilterIfExists('assistantUserRoles', {
        enabled: true,
        value: assistantData?.assistantId,
        operation: ApiFilterOperation.EQ,
        object: await fetchAssistantAutocomplete({
          id: assistantData?.assistantId,
          name: assistantData?.assistant,
        }),
      });
    }
    setTableSettings(tableSettingsId, {
      ...settings,
      version: config.version,
      searchQuery: '',
      filtersState,
    });
    navigate(config.url);
  };

  useEffect(() => {
    if (formattedData?.data?.length) {
      setAssistantData(formattedData.data[0]);
    } else {
      setAssistantData(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedData]);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Grid item xs={12}>
        <DashboardCard>
          <Button
            onClick={() => setYear((p) => p - 1)}
            disabled={allYears || year === MIN_YEAR}
          >
            <ArrowLeft />
          </Button>
          <DatePicker
            views={['year']}
            value={new Date(year, 0, 1)}
            minDate={new Date(MIN_YEAR, 0, 1)}
            maxDate={new Date(MAX_YEAR, 0, 1)}
            onChange={(v) => {
              setYear(v?.getFullYear?.() ?? MIN_YEAR);
            }}
            open={allYears ? false : open}
            openTo="date"
            onClose={() => setOpen(false)}
            TextFieldComponent={function Input() {
              return (
                <Button
                  disabled={allYears}
                  variant="outlined"
                  onClick={() => setOpen(true)}
                  endIcon={<CalendarTodayIcon fontSize="small" />}
                >
                  {year}
                </Button>
              );
            }}
          />
          <Button
            onClick={() => setYear((p) => p + 1)}
            disabled={allYears || year === MAX_YEAR}
          >
            <ArrowRight />
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={allYears}
                color="primary"
                size="small"
                onChange={(event) => setAllYears(event.target.checked)}
              />
            }
            label="Všechny roky"
            labelPlacement="end"
          />
          <Grid container spacing={3}>
            {(formattedData?.data ?? []).length > 0 ? (
              <Grid item xs={5} style={{ placeSelf: 'center' }}>
                {formattedData?.data?.map((a) => (
                  <DashboardButton
                    key={a.assistant}
                    label={a.assistant}
                    value={a.totalCount ?? 0}
                    active={assistantData?.assistant === a.assistant}
                    showAction={true}
                    onClick={() => setAssistantData(a)}
                  />
                ))}
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                style={{
                  placeSelf: 'center',
                  minHeight: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography>Žádní asistenti</Typography>
              </Grid>
            )}
            {assistantData && (
              <Grid item xs={7}>
                <DashboardDivider
                  text={`Spisy - ${assistantData?.assistant} - ${year}`}
                />

                <DashboardBarChart
                  data={[
                    ...(assistantData?.unresolved ?? []),
                    ...(assistantData?.resolved ?? []),
                  ]}
                  onBarClick={handleBarClick}
                />
              </Grid>
            )}
          </Grid>
        </DashboardCard>
      </Grid>
    </MuiPickersUtilsProvider>
  );
}
